/**
 * Authentication service for handling tokens and auth API calls
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TOKEN_KEY = 'systemcraft_token';

/**
 * Store JWT token in localStorage
 */
export const setToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Get stored JWT token
 */
export const getToken = () => {
    return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove JWT token from storage
 */
export const removeToken = () => {
    localStorage.removeItem(TOKEN_KEY);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
    return !!getToken();
};

/**
 * Initiate Google OAuth login
 * Redirects to backend OAuth endpoint
 */
export const loginWithGoogle = () => {
    window.location.href = `${API_URL}/auth/google`;
};

/**
 * Handle OAuth callback - extract token from URL
 */
export const handleAuthCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');

    if (error) {
        return { success: false, error };
    }

    if (token) {
        setToken(token);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return { success: true, token };
    }

    return { success: false };
};

/**
 * Get current user info from API
 */
export const getCurrentUser = async () => {
    const token = getToken();
    if (!token) {
        return null;
    }

    try {
        const response = await fetch(`${API_URL}/auth/me`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            if (response.status === 401) {
                // Token expired or invalid
                removeToken();
                return null;
            }
            throw new Error('Failed to fetch user info');
        }

        return await response.json();
    } catch {
        return null;
    }
};

/**
 * Logout user
 */
export const logout = async () => {
    const token = getToken();

    // Call logout endpoint (optional, for server-side session cleanup if needed)
    if (token) {
        try {
            await fetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
        } catch {
            // Ignore logout API errors
        }
    }

    // Remove token from storage
    removeToken();
};

export default {
    setToken,
    getToken,
    removeToken,
    isAuthenticated,
    loginWithGoogle,
    handleAuthCallback,
    getCurrentUser,
    logout
};
